
import React from "react";
import { 
  Form
} from "@/components/ui/form";
import { 
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from "@/components/ui/dialog";
import { 
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from "@/components/ui/tabs";
import { Client } from "@/models/Client";
import { useClientFormManager } from "./hooks/useClientFormManager";
import ClientDetailsStep from "./ClientDetailsStep";
import SubscriptionsStep from "./SubscriptionsStep";
import { useIsMobile, useIsTablet } from "@/hooks/use-mobile";
import { useTranslation } from "react-i18next";

interface ClientFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialData?: Client;
  organizationId?: string;
}

const ClientForm: React.FC<ClientFormProps> = ({ open, onOpenChange, initialData, organizationId }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isFullScreen = isMobile || isTablet;
  
  const {
    form,
    activeTab,
    setActiveTab,
    workoutTypes,
    isEditMode,
    isLoading,
    hasWorkoutTypes,
    handleNextStep,
    handleBackStep,
    handleCancel,
    handleDetailsSubmit,
    handleSubscriptionsSubmit,
    handleFullSubmit
  } = useClientFormManager({ open, onOpenChange, initialData, organizationId });

  // Check for validation errors in each tab
  const detailsHasErrors = () => {
    const errors = form.formState.errors;
    const detailsFields = ["firstName", "lastName", "email", "birthDate", "gender", "phone", "address", "notes", "paymentStatus"];
    return detailsFields.some(field => errors[field as keyof typeof errors]);
  };

  const subscriptionsHasErrors = () => {
    const errors = form.formState.errors;
    return !!errors.subscriptions;
  };

  // Show error message if no workout types are available
  if (!hasWorkoutTypes && !isEditMode) {
    const errorContent = (
      <div className="p-6 text-center">
        <h3 className="text-lg font-semibold text-destructive mb-2">
          {t('clients.errors.noWorkoutTypes.title')}
        </h3>
        <p className="text-sm text-muted-foreground">
          {t('clients.errors.noWorkoutTypes.description')}
        </p>
      </div>
    );

    if (isFullScreen) {
      return (
        <Sheet open={open} onOpenChange={onOpenChange}>
          <SheetContent 
            side="bottom" 
            className="h-screen w-full rounded-none border-0 p-0 flex flex-col modal-safe-area-aware ios-modal-safe pwa-modal-header-fix"
          >
            <SheetHeader className="p-4 border-b flex-shrink-0">
              <SheetTitle className="text-lg">
                {t('clients.add')}
              </SheetTitle>
            </SheetHeader>
            <div className="flex-1 flex items-center justify-center pb-safe-bottom">
              {errorContent}
            </div>
          </SheetContent>
        </Sheet>
      );
    }

    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{t('clients.add')}</DialogTitle>
          </DialogHeader>
          {errorContent}
        </DialogContent>
      </Dialog>
    );
  }

  const formContent = (
    <Form {...form}>
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full h-full flex flex-col">
        <TabsList className="grid w-full grid-cols-2 mb-6 flex-shrink-0">
          <TabsTrigger value="details" className="text-xs md:text-sm relative">
            <span className="flex items-center gap-2">
              {t('clients.clientDetails')}
              {detailsHasErrors() && (
                <span className="w-2 h-2 bg-destructive rounded-full flex-shrink-0" />
              )}
            </span>
          </TabsTrigger>
          <TabsTrigger value="subscriptions" className="text-xs md:text-sm relative">
            <span className="flex items-center gap-2">
              {t('clients.subscriptions')}
              {subscriptionsHasErrors() && (
                <span className="w-2 h-2 bg-destructive rounded-full flex-shrink-0" />
              )}
            </span>
          </TabsTrigger>
        </TabsList>
        
        <div className="flex-1 overflow-y-auto">
          <TabsContent value="details" className="mt-0 h-full">
            <form onSubmit={form.handleSubmit(isEditMode ? handleDetailsSubmit : handleFullSubmit)} className="space-y-4 md:space-y-6 h-full flex flex-col">
              <div className="flex-1 overflow-y-auto">
                <ClientDetailsStep
                  control={form.control}
                  onNext={isEditMode ? undefined : handleNextStep}
                  onCancel={handleCancel}
                  isEditMode={isEditMode}
                  isLoading={isLoading}
                />
              </div>
            </form>
          </TabsContent>
          
          <TabsContent value="subscriptions" className="mt-0 h-full">
            <form onSubmit={form.handleSubmit(isEditMode ? handleSubscriptionsSubmit : handleFullSubmit)} className="space-y-4 md:space-y-6 h-full flex flex-col">
              <div className="flex-1 overflow-y-auto">
                <SubscriptionsStep
                  control={form.control}
                  workoutTypes={workoutTypes}
                  onBack={isEditMode ? undefined : handleBackStep}
                  onCancel={handleCancel}
                  isLoading={isLoading}
                  isEditMode={isEditMode}
                />
              </div>
            </form>
          </TabsContent>
        </div>
      </Tabs>
    </Form>
  );

  if (isFullScreen) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent 
          side="bottom" 
          className="h-screen w-full rounded-none border-0 p-0 flex flex-col modal-safe-area-aware ios-modal-safe pwa-modal-header-fix"
        >
          <SheetHeader className="p-4 border-b flex-shrink-0">
            <SheetTitle className="text-lg">
              {isEditMode ? t('clients.edit') : t('clients.add')}
            </SheetTitle>
            <SheetDescription className="text-sm">
              {isEditMode 
                ? t('clients.updateClientDescription')
                : t('clients.addClientDescription')
              }
            </SheetDescription>
          </SheetHeader>
          <div className="flex-1 p-4 overflow-hidden pb-safe-bottom">
            {formContent}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditMode ? t('clients.edit') : t('clients.add')}</DialogTitle>
          <DialogDescription>
            {isEditMode 
              ? t('clients.updateClientDescription')
              : t('clients.addClientDescription')
            }
          </DialogDescription>
        </DialogHeader>
        
        {formContent}
      </DialogContent>
    </Dialog>
  );
};

export default ClientForm;
